import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { FileSignature, ShieldCheck, Wand2, PenLine, Archive, CreditCard, Copy } from "lucide-react";
import { AiGeneratorPage } from "@/components/marketing/AiGeneratorPage";

export const Route = createFileRoute("/vi/ai/contract-generator")({
  head: () => ({
    meta: [
      { title: "Trình tạo hợp đồng AI — Banana Cards" },
      { name: "description", content: "Soạn thảo hợp đồng chuyên nghiệp bằng AI trong vài giây." },
    ],
    links: alternateLinkList("/ai/contract-generator", "vi"),
  }),
  component: Page,
});

const EXAMPLES = [
  "NDA song phương giữa một freelancer thiết kế và một startup SaaS, áp dụng luật Việt Nam",
  "Hợp đồng dịch vụ thiết kế web: 8 tuần, 250 triệu, cọc 50%, 2 vòng chỉnh sửa",
  "Hợp đồng chụp ảnh cưới tại Đà Nẵng, 8 giờ chụp, giao album online",
  "Hợp đồng cộng tác viên marketing bán thời gian, 2 triệu/giờ, thời hạn 6 tháng",
  "Hợp đồng thuê thiết bị DJ cuối tuần, cọc 10 triệu, có điều khoản bồi thường hỏng hóc",
];

export function Page() {
  return (
    <AiGeneratorPage
      eyebrow="Trình tạo hợp đồng AI"
      title="Soạn hợp đồng"
      italic="trong vài giây."
      description="Cho chúng tôi biết bạn cần gì. Banana soạn hợp đồng gọn gàng, sẵn sàng ký, bạn có thể chỉnh sửa, gắn thương hiệu và gửi để ký điện tử — dựa trên các mẫu đã được luật sư rà soát."
      headline="Bạn muốn tạo loại hợp đồng nào?"
      placeholders={EXAMPLES}
      linkedFromHref="/vi/platform/contracts"
      linkedFromLabel="Hợp đồng & Ký điện tử Banana"
      sections={[
        { icon: Wand2, title: "Nhập bằng tiếng Việt tự nhiên", desc: "Mô tả thoả thuận bằng lời của bạn. Banana chọn đúng mẫu, các bên và điều khoản phù hợp." },
        { icon: FileSignature, title: "Ký điện tử chỉ 1 chạm", desc: "Gửi PDF để ký ngay từ workspace Banana — không cần công cụ nào khác." },
        { icon: ShieldCheck, title: "Hợp pháp và chặt chẽ", desc: "Xây trên mẫu đã được luật sư rà soát. Tuân thủ US ESIGN, UETA và EU eIDAS." },
      ]}
      capabilities={[
        "NDA, MSA, SOW, hợp đồng freelance & retainer",
        "Tự động điền tên các bên, ngày, giá và luật áp dụng",
        "Điều khoản có điều kiện (phạt trễ hạn, IP, phí huỷ)",
        "Chỉnh sửa trong trình soạn thảo trước khi gửi",
        "Nhật ký kiểm toán chống giả mạo cho PDF đã ký",
        "Lưu trữ trong kho hợp đồng riêng của bạn",
        "Mẫu tái sử dụng cho từng khách hàng hoặc danh mục",
        "Xuất ra .pdf hoặc .docx",
      ]}
      howItWorks={[
        { title: "Mô tả thoả thuận", desc: "Gõ 1–2 câu — các bên, phạm vi, giá, thời hạn." },
        { title: "AI soạn hợp đồng", desc: "Chọn mẫu phù hợp và cá nhân hoá từng điều khoản." },
        { title: "Chỉnh sửa & gắn thương hiệu", desc: "Sửa từng dòng, thêm logo, đính kèm phụ lục." },
        { title: "Gửi để ký điện tử", desc: "Người ký chỉ cần chạm vào link trên thiết bị bất kỳ — không cần tài khoản." },
      ]}
      faq={[
        { q: "Hợp đồng được tạo có ràng buộc pháp lý không?", a: "Có — Banana soạn dựa trên các mẫu đã được luật sư rà soát và mọi chữ ký thu qua nền tảng đều tuân thủ US ESIGN, UETA và EU eIDAS. Với các thoả thuận quan trọng, chúng tôi luôn khuyên bạn nhờ luật sư của mình rà soát lần cuối." },
        { q: "AI có thể tạo những loại hợp đồng nào?", a: "NDA song phương và một chiều, MSA, SOW, hợp đồng freelance và tư vấn, hợp đồng dịch vụ, retainer, hợp đồng chụp ảnh và sự kiện, cho thuê thiết bị, thư mời làm việc, hợp đồng hợp tác và đại lý, giấy miễn trừ và đồng ý — bất kỳ tài liệu nào bạn thường gửi dưới dạng PDF." },
        { q: "Tôi có cần tài khoản Banana để dùng thử không?", a: "Không — công cụ tạo phía trên miễn phí sử dụng ngay bây giờ. Bạn chỉ cần đăng ký nếu muốn lưu hợp đồng, chỉnh sửa trong trình soạn thảo hoặc gửi để ký điện tử." },
        { q: "Tôi có thể dùng mẫu của riêng mình không?", a: "Có — gói trả phí cho phép tải lên .docx hoặc .pdf làm mẫu, sau đó dùng AI cá nhân hoá cho từng khách với placeholder như {{ten_khach}}, {{so_tien}} và {{ngay_bat_dau}}." },
        { q: "Trình tạo hợp đồng hỗ trợ ngôn ngữ nào?", a: "Có thể soạn và dịch hợp đồng trong hơn 50 ngôn ngữ gồm tiếng Việt, tiếng Anh, Tây Ban Nha, Pháp, Đức, Trung, Nhật, Hàn — chỉ cần viết prompt bằng ngôn ngữ đó." },
      ]}
      outroCta={{ to: "/signup", label: "Bắt đầu miễn phí" }}
      flow={{
        labels: {
          next: "Tiếp",
          back: "Quay lại",
          generate: "Tạo ngay",
          step: "Bước {n} / {total}",
          nextThingsHeading: "Bạn còn có thể làm gì",
        },
        steps: [
          {
            question: "Loại hợp đồng nào?",
            multi: false,
            options: ["NDA (bảo mật)", "Freelance / Cộng tác viên", "MSA / SOW", "Lao động", "Chụp ảnh", "Thuê / Cho thuê", "Hợp tác", "Khác"],
          },
          {
            question: "Ai tham gia?",
            multi: false,
            options: ["Tôi và 1 bên khác", "Nhiều người ký", "Công ty ↔ Công ty", "Công ty ↔ Cá nhân"],
          },
          {
            question: "Cần có thêm điều khoản nào?",
            options: ["Điều khoản thanh toán", "Chuyển giao quyền sở hữu trí tuệ", "Bảo mật", "Chấm dứt hợp đồng", "Luật áp dụng", "Phí trễ hạn"],
          },
        ],
        loadingMessages: [
          "Đang xem xét yêu cầu của bạn…",
          "Đang chọn mẫu phù hợp…",
          "Đang soạn các điều khoản…",
          "Đang áp dụng quy định pháp lý…",
          "Đang định dạng để ký điện tử…",
          "Đang thêm dữ liệu nhật ký kiểm toán…",
          "Sắp xong — đang trau chuốt ngôn từ…",
        ],
        readyTitle: "Hợp đồng của bạn đã sẵn sàng.",
        readyBody: "Banana AI đã soạn một hợp đồng hoàn chỉnh, sẵn sàng ký dựa trên câu trả lời của bạn. Đăng ký để xem, chỉnh sửa, gắn thương hiệu và gửi ký điện tử.",
        ctaLabel: "Đăng ký để xem",
        ctaHref: "https://app.banana.cards/signup",
        nextThings: [
          { icon: PenLine, title: "Ký điện tử 1 chạm", desc: "Gửi ký từ mọi thiết bị — người ký không cần tài khoản." },
          { icon: Archive, title: "Lưu vào kho hợp đồng", desc: "Mọi hợp đồng được lưu kèm nhật ký kiểm toán chống giả mạo." },
          { icon: CreditCard, title: "Thu tiền khi ký", desc: "Đính kèm liên kết thanh toán đặt cọc hoặc toàn bộ vào hợp đồng." },
          { icon: Copy, title: "Dùng lại làm mẫu", desc: "Lưu bản nháp này thành mẫu cho từng khách hoặc loại việc." },
        ],
      }}
    />
  );
}